import { ArrowLeft, Search, Home, MessageCircle } from "lucide-react";
import { BLOG_POSTS, type BlogPost } from "@/lib/blog-posts";
import { KENTRO } from "@/lib/constants";

type Props = {
  slug?: string;
  locale?: "pt" | "en" | "es";
};

const COPY = {
  pt: {
    tag: "Erro 404",
    title: "Post não encontrado",
    lead: (s?: string) =>
      s
        ? `Não encontramos nenhum conteúdo para "${s}". Talvez o link esteja desatualizado ou o post tenha sido movido.`
        : "O conteúdo que você procura não existe ou foi movido.",
    suggestions: "Enquanto isso, veja os posts mais recentes",
    backBlog: "Voltar ao blog",
    home: "Ir para a home",
    talk: "Falar no WhatsApp",
    minutes: "min",
    by: "Por",
  },
  en: {
    tag: "404 error",
    title: "Post not found",
    lead: (s?: string) =>
      s
        ? `We couldn't find anything for "${s}". The link may be outdated or the post moved.`
        : "The content you're looking for doesn't exist or has been moved.",
    suggestions: "Meanwhile, check the latest posts",
    backBlog: "Back to blog",
    home: "Go home",
    talk: "Chat on WhatsApp",
    minutes: "min",
    by: "By",
  },
  es: {
    tag: "Error 404",
    title: "Publicación no encontrada",
    lead: (s?: string) =>
      s
        ? `No encontramos nada para "${s}". El enlace puede estar desactualizado o la publicación fue movida.`
        : "El contenido que buscas no existe o fue movido.",
    suggestions: "Mientras tanto, mira las publicaciones más recientes",
    backBlog: "Volver al blog",
    home: "Ir al inicio",
    talk: "Hablar por WhatsApp",
    minutes: "min",
    by: "Por",
  },
} as const;

export function BlogNotFound({ slug, locale = "pt" }: Props) {
  const t = COPY[locale];
  const blogHref = locale === "pt" ? "/blog" : `/${locale}/blog`;
  const homeHref = locale === "pt" ? "/" : `/${locale}`;

  const suggestions: BlogPost[] = [...BLOG_POSTS]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 3);

  return (
    <section className="mx-auto max-w-3xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <div className="rounded-2xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
          <Search className="size-3.5" /> {t.tag}
        </span>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
          {t.title}
        </h1>
        <p className="mt-3 text-base text-muted-foreground md:text-lg">{t.lead(slug)}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          <a
            href={blogHref}
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
          >
            <ArrowLeft className="size-4" /> {t.backBlog}
          </a>
          <a
            href={homeHref}
            className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
          >
            <Home className="size-4" /> {t.home}
          </a>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
          >
            <MessageCircle className="size-4" /> {t.talk}
          </a>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t.suggestions}
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suggestions.map((p) => (
            <a
              key={p.slug}
              href={`${blogHref}/${p.slug}`}
              className="group rounded-xl border border-border bg-card/40 p-5 transition hover:border-primary-glow/40"
            >
              <span className="text-xs text-muted-foreground">{p.category}</span>
              <h3 className="mt-2 font-semibold group-hover:text-primary-glow">{p.title}</h3>
              <p className="mt-2 text-xs text-muted-foreground">
                {p.readingMinutes} {t.minutes} · {t.by} {p.author}
              </p>
            </a>
          ))}
        </div>
      </section>
    </section>
  );
}

type ErrorProps = {
  error: Error;
  reset: () => void;
  locale?: "pt" | "en" | "es";
};

const ERR_COPY = {
  pt: {
    tag: "Algo deu errado",
    title: "Não conseguimos carregar este post",
    lead: "Tivemos um problema ao abrir esse conteúdo. Você pode tentar novamente ou voltar ao blog.",
    retry: "Tentar novamente",
    backBlog: "Voltar ao blog",
    home: "Ir para a home",
  },
  en: {
    tag: "Something went wrong",
    title: "We couldn't load this post",
    lead: "Something failed while opening this content. Try again or head back to the blog.",
    retry: "Try again",
    backBlog: "Back to blog",
    home: "Go home",
  },
  es: {
    tag: "Algo salió mal",
    title: "No pudimos cargar esta publicación",
    lead: "Ocurrió un problema al abrir este contenido. Puedes reintentar o volver al blog.",
    retry: "Reintentar",
    backBlog: "Volver al blog",
    home: "Ir al inicio",
  },
} as const;

export function BlogErrorState({ error, reset, locale = "pt" }: ErrorProps) {
  if (typeof console !== "undefined") console.error("[blog] render error:", error);
  const t = ERR_COPY[locale];
  const blogHref = locale === "pt" ? "/blog" : `/${locale}/blog`;
  const homeHref = locale === "pt" ? "/" : `/${locale}`;
  return (
    <section className="mx-auto max-w-2xl px-4 pt-24 pb-16 text-center sm:px-6">
      <span className="inline-flex rounded-full border border-border bg-card/40 px-2.5 py-1 text-xs text-muted-foreground">
        {t.tag}
      </span>
      <h1 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">{t.title}</h1>
      <p className="mt-3 text-muted-foreground">{t.lead}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button
          onClick={reset}
          className="gradient-primary inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-glow hover:opacity-95"
        >
          {t.retry}
        </button>
        <a
          href={blogHref}
          className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
        >
          <ArrowLeft className="size-4" /> {t.backBlog}
        </a>
        <a
          href={homeHref}
          className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
        >
          <Home className="size-4" /> {t.home}
        </a>
      </div>
    </section>
  );
}
